import {
  type AbstractMessage,
  type ACCOUNT_TYPE,
  type AccountInformation,
  type BaseCurrencyControllerState,
  type BaseEmbedControllerState,
  type BaseTokenInfo,
  type BaseTokenRatesControllerState,
  type CONFIRMATION_STRATEGY_TYPE,
  type CustomNftInfo,
  LoginMethodConfig,
  type ProviderConfig,
  type TransactionMeta,
  TransactionStatus,
} from "@toruslabs/base-controllers";
import { type AccountAbstractionMultiChainConfig, type EthereumGasState } from "@toruslabs/ethereum-controllers";
import type { AUTH_CONNECTION_TYPE, AuthConnectionConfig, ExtraLoginOptions, WEB3AUTH_NETWORK_TYPE, WhiteLabelData } from "@web3auth/auth";
import { Hex } from "viem";

export const TOAST_TYPES = {
  SUCCESS: "success",
  ERROR: "danger",
  INFO: "info",
} as const;

export const APP_PLATFORMS = {
  ANDROID: "android",
  IOS: "ios",
  NATIVE: "react-native",
  FLUTTER: "flutter",
  UNITY: "unity",
  UNREAL: "unreal",
  EMBED: "embed",
} as const;

export type APP_PLATFORMS_TYPE = (typeof APP_PLATFORMS)[keyof typeof APP_PLATFORMS];

export interface BaseLoginParams {
  sessionId: string;
  sessionNamespace?: string;
  loginId?: Hex;
  request?: { method: string; params: unknown[] };
  platform?: APP_PLATFORMS_TYPE;
}

export interface AuthState {
  instanceId: string;
  web3AuthNetwork: WEB3AUTH_NETWORK_TYPE;
  web3AuthClientId: string;
  wsLoginId: Hex;
  whiteLabel?: WhiteLabelData;
}

export interface AuthLoginPayload {
  authConnection: AUTH_CONNECTION_TYPE;
  authConnectionId?: string;
  groupedAuthConnectionId?: string;
  authConnectionConfig?: AuthConnectionConfig;
  state: string;
  windowId: string;
  sessionNamespace?: string;
  extraLoginOptions?: ExtraLoginOptions;
}

export const LOGIN_MODE = {
  main: "main",
  embed: "embed",
  plugin: "plugin",
  mobile_sdk: "mobile_sdk",
} as const;

export type LOGIN_MODE_TYPE = (typeof LOGIN_MODE)[keyof typeof LOGIN_MODE];

export interface SwapTransactionParams {
  data: string;
  to: string;
  value: string;
  from: string;
  gasPrice?: string;
  maxFeePerGas?: string;
  maxPriorityFeePerGas?: string;
}

export type AuthPopupResponse = {
  sessionId: string;
};

export interface EmbedInitParams {
  chains: ProviderConfig[];
  chainId: string;
  dappMetadata: {
    name: string;
    icon: string;
  };
  enableLogging?: boolean;
  web3AuthClientId: string;
  web3AuthNetwork: WEB3AUTH_NETWORK_TYPE;
  authConnectionConfig?: AuthConnectionConfig;
  loginMethodConfig?: LoginMethodConfig;
  confirmationStrategy?: CONFIRMATION_STRATEGY_TYPE;
  whiteLabel?: BaseEmbedControllerState["whiteLabel"];
  accountAbstractionConfig?: AccountAbstractionMultiChainConfig;
  enableKeyExport?: boolean;
  enableLegacyConfig?: boolean;
  gating: {
    allowWalletService: boolean;
    errorMessage: string;
  };
}

export type ToastMessage = {
  type?: (typeof TOAST_TYPES)[keyof typeof TOAST_TYPES];
  message: string;
};

export type ProviderConfigOption = ProviderConfig & { isCustom?: boolean; id: number };

export interface Account {
  privKey: string;
  accountType: ACCOUNT_TYPE;
  address: string;
  dappIds?: number[];
}

export interface MessageChannelEventData {
  selectedAddress: string;
  whitelabelData: BaseEmbedControllerState["whiteLabel"];
  msgParams: AbstractMessage<unknown> | TransactionMeta<unknown> | TransactionMeta<unknown>[];
  networkDetails: ProviderConfig;
  origin: string;
  gasFeeState?: EthereumGasState;
  currencyState: BaseCurrencyControllerState;
  tokensState: BaseTokenInfo[];
  tokenRatesState: BaseTokenRatesControllerState;
  nftsState: CustomNftInfo[];
  accountsState: AccountInformation;
  dappMetadata: BaseEmbedControllerState["dappMetadata"];
  isFirstTransaction?: boolean;
}

export interface GasFeeModalData {
  gasLimit: string;
  gasPrice: string;
  maxPriorityFee: string;
  maxTransactionFee: string;
  nonce: string;
  speedValue: string;
  description: string;
  baseFee: string;
}

export type StorageUtils = { storage: Storage; storageType: "local" | "session"; storageKey: string; authStoreKey: string };

export interface FetchQuoteParams {
  fromToken: string;
  toToken: string;
  fromValue: string;
  toAddress: string;
  slippageTolerance?: string;
  transactionDeadline?: number;
}

export interface DashboardUserProject {
  project_id: string;
  name: string;
  last_login: string;
  hostname: string;
}

export interface BaseFormattedTransactionActivity {
  id: string;
  date: string;
  from: string;
  slicedFrom: string;
  to: string;
  slicedTo: string;
  action: string;
  totalAmount: string;
  totalAmountString: string;
  currencyAmount: string;
  currencyAmountString: string;
  amount: string;
  status: TransactionStatus;
  currencyUsed: string;
  chainId: string;
  type: string;
  type_name: string;
  type_image_link: string;
  transaction_hash: string;
  transaction_category: string;
  input?: string;
  contract_address?: string;
  token_id?: string;
  is_cancel: boolean;
  hasCancel?: boolean;
  cancelDateInitiated?: string;
}
